import { zodResolver } from "@hookform/resolvers/zod";
import {
  type CreateInvitationInput,
  type CreateInvitationResponse,
  createInvitationSchema,
  hasRole,
  INVITATION_EXPIRY_DAYS,
  type InvitationDto,
  type InvitationStatus,
  type Locale,
  type SendInvitationResponse,
} from "@shelflist/shared";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { MailPlus, Plus } from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { z } from "zod";
import {
  CheckboxRow,
  DeliveryAlert,
  LocaleSelect,
  localeName,
  useDeliveryErrorText,
  useLinkEmails,
  useUiLocale,
} from "@/components/admin/link-email";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { CopyField } from "@/components/copy-field";
import { Field, fieldA11y } from "@/components/field";
import { FormError } from "@/components/form-error";
import { Page } from "@/components/page";
import { ResponsiveTable } from "@/components/responsive-table";
import { RoleSelect } from "@/components/role-select";
import { EmptyState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useFormat } from "@/i18n/format";
import { api, call } from "@/lib/api";
import { applyFieldErrors, useErrorMessage } from "@/lib/errors";
import { adminEmailQuery } from "@/lib/kindle";
import { invitationsQuery, queryKeys } from "@/lib/queries";

export const Route = createFileRoute("/_app/admin/invitations")({
  // Non-admins get the NoPermission page from the layout; do not fire a request that would 403.
  loader: async ({ context }) => {
    if (!hasRole(context.me, "admin")) return;
    // The email status only decides which send options appear; a failure must not block the page.
    void context.queryClient.prefetchQuery(adminEmailQuery);
    await context.queryClient.ensureQueryData(invitationsQuery);
  },
  component: InvitationsPage,
});

export function InvitationsPage() {
  const { t } = useTranslation(["admin", "common"]);
  const format = useFormat();
  const queryClient = useQueryClient();
  const { data } = useSuspenseQuery(invitationsQuery);
  const linkEmails = useLinkEmails();
  const revoke = useMutation({
    mutationFn: (id: string) => call(api.invitations[":id"].revoke.$post({ param: { id } })),
    onSuccess: () => {
      toast.success(t("invitations.revoked"));
      return queryClient.invalidateQueries({ queryKey: queryKeys.invitations });
    },
  });

  return (
    <Page
      title={t("invitations.title")}
      description={t("invitations.description")}
      actions={<CreateInvitationDialog />}
    >
      {data.invitations.length === 0 ? (
        <EmptyState icon={MailPlus} title={t("invitations.empty")} />
      ) : (
        <ResponsiveTable
          rows={data.invitations}
          rowKey={(invitation) => invitation.id}
          actionsHeader={t("invitations.columns.actions")}
          columns={[
            {
              id: "email",
              header: t("invitations.columns.email"),
              primary: true,
              cell: (invitation) => (
                <>
                  {invitation.email ?? (
                    <span className="text-muted-foreground">{t("invitations.anyEmail")}</span>
                  )}
                  {invitation.createdBy ? (
                    <span className="text-muted-foreground block text-xs">
                      {t("invitations.createdByName", { name: invitation.createdBy.name })}
                    </span>
                  ) : null}
                  <DeliveryLine invitation={invitation} />
                </>
              ),
            },
            {
              id: "role",
              header: t("invitations.columns.role"),
              cell: (invitation) => t(`common:roles.${invitation.role}`),
            },
            {
              id: "status",
              header: t("invitations.columns.status"),
              cell: (invitation) => <StatusBadge status={invitation.status} />,
            },
            {
              id: "createdAt",
              header: t("invitations.columns.createdAt"),
              cell: (invitation) => format.dateTime(invitation.createdAt),
            },
            {
              id: "expiresAt",
              header: t("invitations.columns.expiresAt"),
              cell: (invitation) => format.dateTime(invitation.expiresAt),
            },
          ]}
          actions={(invitation) =>
            invitation.status === "pending" ? (
              <div className="flex flex-wrap gap-2">
                {invitation.email && linkEmails.available ? (
                  <SendAgainDialog invitation={invitation} email={invitation.email} />
                ) : null}
                <ConfirmDialog
                  trigger={
                    <Button variant="outline" size="sm" disabled={revoke.isPending}>
                      {t("common:actions.revoke")}
                    </Button>
                  }
                  title={t("invitations.revokeTitle")}
                  description={t("invitations.revokeDescription")}
                  confirmLabel={t("common:actions.revoke")}
                  destructive
                  onConfirm={() => revoke.mutate(invitation.id)}
                />
              </div>
            ) : null
          }
        />
      )}
    </Page>
  );
}

/** Second line of the email cell: outcome of the last send, language and number of attempts. */
function DeliveryLine({ invitation }: { invitation: InvitationDto }) {
  const { t } = useTranslation("admin");
  const format = useFormat();
  const errorText = useDeliveryErrorText();
  const delivery = invitation.emailDelivery;
  if (!delivery) return null;
  const failed = delivery.status === "failed";
  const time = format.dateTime(delivery.lastAttemptAt);
  const text =
    delivery.status === "pending"
      ? t("invitations.delivery.pending")
      : delivery.status === "sent"
        ? t("invitations.delivery.sent", { time })
        : delivery.errorCode === "delivery_interrupted"
          ? t("invitations.delivery.interrupted", { time })
          : t("invitations.delivery.failed", { time, error: errorText(delivery.errorCode) });
  return (
    <span className="mt-0.5 block text-xs" title={delivery.diagnostic ?? undefined}>
      <span className={failed ? "text-destructive" : "text-muted-foreground"}>{text}</span>
      {failed && delivery.diagnostic ? (
        <code className="text-muted-foreground block break-all">{delivery.diagnostic}</code>
      ) : null}
      <span className="text-muted-foreground block">
        {localeName(delivery.locale)}
        {delivery.sendCount > 1
          ? ` \u00b7 ${t("invitations.delivery.sentCount", { count: delivery.sendCount })}`
          : ""}
      </span>
    </span>
  );
}

/** "Send again": confirms the link rotation, then shows the new link and the delivery result. */
function SendAgainDialog({ invitation, email }: { invitation: InvitationDto; email: string }) {
  const { t } = useTranslation(["admin", "common"]);
  const queryClient = useQueryClient();
  const errorMessage = useErrorMessage();
  const uiLocale = useUiLocale();
  const [open, setOpen] = useState(false);
  const [locale, setLocale] = useState<Locale>(invitation.emailDelivery?.locale ?? uiLocale);
  const [result, setResult] = useState<SendInvitationResponse | null>(null);
  const send = useMutation({
    mutationFn: () =>
      call(api.invitations[":id"].send.$post({ param: { id: invitation.id }, json: { locale } })),
    meta: { handlesErrors: true },
    // The token is replaced even when the mail fails, and a refused request changes the state too.
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.invitations }),
    onSuccess: setResult,
  });

  function forget() {
    // The new link is shown exactly once; the next opening starts from the confirmation.
    setResult(null);
    send.reset();
  }

  function onOpenChange(next: boolean) {
    // The link is created on the server while sending; keep the dialog until the outcome is shown.
    if (!next && send.isPending) return;
    if (next) forget();
    setOpen(next);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          {t("invitations.sendAgain")}
        </Button>
      </DialogTrigger>
      <DialogContent showCloseButton={!send.isPending} onCloseAutoFocus={forget}>
        {result ? (
          <>
            <DialogHeader>
              <DialogTitle>{t("invitations.sentAgainTitle")}</DialogTitle>
              <DialogDescription>{t("invitations.createdDescription")}</DialogDescription>
            </DialogHeader>
            <DeliveryAlert
              result={result.email}
              success={t("invitations.sendSuccess", { email })}
              failed={(error) => t("invitations.sendFailed", { error })}
            />
            <CopyField value={result.url} label={t("invitations.link")} />
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>{t("common:actions.done")}</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{t("invitations.sendAgainTitle")}</DialogTitle>
              <DialogDescription>
                {t("invitations.sendAgainDescription", { email })}
              </DialogDescription>
            </DialogHeader>
            <FormError message={send.error ? errorMessage(send.error) : null} />
            <LocaleSelect
              id={`send-again-locale-${invitation.id}`}
              value={locale}
              onChange={setLocale}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={send.isPending}
                onClick={() => onOpenChange(false)}
              >
                {t("common:actions.cancel")}
              </Button>
              <Button type="button" disabled={send.isPending} onClick={() => send.mutate()}>
                {send.isPending ? t("invitations.sending") : t("invitations.sendAgainSubmit")}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatusBadge({ status }: { status: InvitationStatus }) {
  const { t } = useTranslation(["admin", "common"]);
  const variant =
    status === "pending" ? "default" : status === "accepted" ? "secondary" : "outline";
  return <Badge variant={variant}>{t(`invitations.status.${status}`)}</Badge>;
}

type FormValues = z.input<typeof createInvitationSchema>;

function CreateInvitationDialog() {
  const { t } = useTranslation(["admin", "common"]);
  const queryClient = useQueryClient();
  const errorMessage = useErrorMessage();
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState<CreateInvitationResponse | null>(null);
  const linkEmails = useLinkEmails();
  const uiLocale = useUiLocale();
  const [sendEmail, setSendEmail] = useState(false);
  // Once the admin touches the checkbox, typing an address no longer flips it.
  const [sendTouched, setSendTouched] = useState(false);
  const [locale, setLocale] = useState<Locale>(uiLocale);
  const form = useForm<FormValues>({
    resolver: zodResolver(createInvitationSchema),
    defaultValues: { role: "reader", email: "", expiresInDays: 7 },
  });
  const create = useMutation({
    mutationFn: (values: CreateInvitationInput) => call(api.invitations.$post({ json: values })),
    meta: { handlesErrors: true },
    onSuccess: (result) => {
      setCreated(result);
      void queryClient.invalidateQueries({ queryKey: queryKeys.invitations });
    },
    onError: (error) => {
      applyFieldErrors(error, form.setError, ["role", "email", "expiresInDays"]);
    },
  });
  const { errors } = form.formState;
  const emailField = form.register("email");
  const emailValue = form.watch("email") ?? "";
  const hasEmail = emailValue.trim() !== "";
  const sending = linkEmails.available && hasEmail && sendEmail;

  function onEmailChange(event: React.ChangeEvent<HTMLInputElement>) {
    void emailField.onChange(event);
    const next = event.target.value.trim() !== "";
    if (!next) {
      setSendEmail(false);
      setSendTouched(false);
    } else if (!hasEmail && !sendTouched) {
      setSendEmail(true);
    }
  }

  function forget() {
    // The link is shown exactly once; the next opening starts from the empty form.
    setCreated(null);
    create.reset();
    form.reset();
    setSendEmail(false);
    setSendTouched(false);
    setLocale(uiLocale);
  }

  function onOpenChange(next: boolean) {
    // The invitation is created on the server while sending; keep the dialog until the outcome is shown.
    if (!next && create.isPending) return;
    if (next) forget();
    setOpen(next);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Plus aria-hidden />
          {t("invitations.create")}
        </Button>
      </DialogTrigger>
      <DialogContent showCloseButton={!create.isPending} onCloseAutoFocus={forget}>
        {created ? (
          <>
            <DialogHeader>
              <DialogTitle>{t("invitations.createdTitle")}</DialogTitle>
              <DialogDescription>{t("invitations.createdDescription")}</DialogDescription>
            </DialogHeader>
            {created.email ? (
              <DeliveryAlert
                result={created.email}
                success={t("invitations.sendSuccess", { email: created.invitation.email ?? "" })}
                failed={(error) => t("invitations.sendFailed", { error })}
              />
            ) : null}
            <CopyField value={created.url} label={t("invitations.link")} />
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>{t("common:actions.done")}</Button>
            </DialogFooter>
          </>
        ) : (
          <form
            noValidate
            className="grid gap-4"
            onSubmit={form.handleSubmit((values) =>
              create.mutate(
                sending
                  ? ({ ...values, sendEmail: true, locale } as CreateInvitationInput)
                  : values,
              ),
            )}
          >
            <DialogHeader>
              <DialogTitle>{t("invitations.createTitle")}</DialogTitle>
              <DialogDescription>{t("invitations.createDescription")}</DialogDescription>
            </DialogHeader>
            <FormError
              message={
                create.error && Object.keys(errors).length === 0 ? errorMessage(create.error) : null
              }
            />
            <Field id="invite-role" label={t("common:fields.role")} error={errors.role?.message}>
              <Controller
                control={form.control}
                name="role"
                render={({ field }) => (
                  <RoleSelect
                    id="invite-role"
                    value={field.value}
                    onChange={field.onChange}
                    className="w-full"
                  />
                )}
              />
            </Field>
            <Field
              id="invite-email"
              label={t("invitations.emailOptional")}
              error={errors.email?.message}
              hint={
                <>
                  <span className="block">{t("invitations.emailHint")}</span>
                  {!linkEmails.available && linkEmails.reason === "public_url_not_configured" ? (
                    <span className="block">{t("invitations.publicUrlHint")}</span>
                  ) : null}
                </>
              }
            >
              <Input
                {...fieldA11y("invite-email", errors.email?.message)}
                type="email"
                autoComplete="off"
                {...emailField}
                onChange={onEmailChange}
              />
            </Field>
            {linkEmails.available ? (
              <>
                <CheckboxRow
                  id="invite-send"
                  checked={sending}
                  disabled={!hasEmail}
                  onChange={(checked) => {
                    setSendEmail(checked);
                    setSendTouched(true);
                  }}
                  label={t("invitations.sendEmail")}
                  hint={t("invitations.sendEmailHint")}
                />
                {sending ? (
                  <LocaleSelect id="invite-locale" value={locale} onChange={setLocale} />
                ) : null}
              </>
            ) : null}
            <Field
              id="invite-expiry"
              label={t("invitations.expiry")}
              error={errors.expiresInDays?.message}
            >
              <Controller
                control={form.control}
                name="expiresInDays"
                render={({ field }) => (
                  <Select
                    value={String(field.value ?? 7)}
                    onValueChange={(value) => field.onChange(Number(value))}
                  >
                    <SelectTrigger id="invite-expiry" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INVITATION_EXPIRY_DAYS.map((days) => (
                        <SelectItem key={days} value={String(days)}>
                          {t("invitations.expiryDays", { count: days })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </Field>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={create.isPending}
                onClick={() => onOpenChange(false)}
              >
                {t("common:actions.cancel")}
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending && sending
                  ? t("invitations.sending")
                  : sending
                    ? t("invitations.createAndSend")
                    : t("invitations.createSubmit")}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
